import AirDatepicker from 'air-datepicker'
import tippy, { hideAll } from 'tippy.js'
import moment from 'moment/moment.js'
import { isTouchDevice } from './tools.js'

export default class BHubCalendar extends AirDatepicker {
  constructor(el, args = {}) {
    const { sprintf } = wp.i18n
    let defaultLocale = el.getAttribute('data-locale')
    defaultLocale = defaultLocale ? JSON.parse(defaultLocale) : null

    const defaultOptions = {
      dateFormat: bookingHub.airDatepickerFormat ?? 'yyyy-MM-dd',
      classes: 'bhub-airdatepicker',
      locale: defaultLocale ?? bookingHub.calendar.i18n,
      firstDay: bookingHub.firstDayOfTheWeek * 1,
      showOtherMonths: true,
      selectOtherMonths: false,
      minDate: new Date(),
      timeFormat: bookingHub.timeFormat.toLowerCase().includes('a')
        ? 'hh:mm AA'
        : 'HH:mm',
      minRange: 0,
      maxRange: 0,
      onBeforeSelect: ({ date, datepicker }) => {
        if (!args.range) {
          return true
        }

        if (BHubCalendar.isRangeIncludesDisabledDay(date, datepicker)) {
          return false
        }

        if (BHubCalendar.isOutOfLimit(date, datepicker, 'both')) {
          return false
        }

        return true
      },
      onFocus: ({ date, datepicker }) => {
        if (!args.range) {
          return
        }

        const { selectedDates } = datepicker
        const monthIndex = date.getMonth()
        const dayIndex = date.getDate()
        const cellEl = datepicker.$datepicker.querySelector(
          `[data-month="${monthIndex}"][data-date="${dayIndex}"]`
        )
        let range = []
        let disabledRange = false

        if (selectedDates.length === 1) {
          range = [selectedDates[0], date]
        }

        if (selectedDates.length === 2) {
          range = selectedDates
        }

        const dayCount = this.dayCount(range)
        let toolTipText = ''

        if (BHubCalendar.isRangeIncludesDisabledDay(date, datepicker)) {
          datepicker.$datepicker.classList.add('-disabled-range-')
          disabledRange = true
          toolTipText = args.rangeHasDisabledDayTooltipMsg ?? ''
        } else if (BHubCalendar.isOutOfLimit(date, datepicker, 'max')) {
          datepicker.$datepicker.classList.add('-disabled-range-')
          disabledRange = true
          if (dayCount === 1) {
            toolTipText = args.rangeDisabledTooltipMaxLimitMsgSingular
              ? sprintf(
                  args.rangeDisabledTooltipMaxLimitMsgSingular,
                  args.maxRange
                )
              : toolTipText
          } else {
            toolTipText = args.rangeDisabledTooltipMaxLimitMsgPlural
              ? sprintf(
                  args.rangeDisabledTooltipMaxLimitMsgPlural,
                  args.maxRange
                )
              : toolTipText
          }
        } else if (BHubCalendar.isOutOfLimit(date, datepicker, 'min')) {
          datepicker.$datepicker.classList.add('-disabled-range-')
          disabledRange = true
          if (dayCount === 1) {
            toolTipText = args.rangeDisabledTooltipMinLimitMsgSingular
              ? sprintf(
                  args.rangeDisabledTooltipMinLimitMsgSingular,
                  args.minRange
                )
              : toolTipText
          } else {
            toolTipText = args.rangeDisabledTooltipMinLimitMsgPlural
              ? sprintf(
                  args.rangeDisabledTooltipMinLimitMsgPlural,
                  args.minRange
                )
              : toolTipText
          }
        } else {
          datepicker.$datepicker.classList.remove('-disabled-range-')
          disabledRange = false
        }

        if (args.nightCounter) {
          let show = true

          if (!range) {
            show = false
          }

          if (
            selectedDates.length === 2 &&
            (date < selectedDates[0] || date > selectedDates[1])
          ) {
            show = false
          }

          if (isTouchDevice()) {
            show = false
          }

          if (show && dayCount > 0) {
            if (!disabledRange) {
              if (dayCount > 1) {
                toolTipText = args.nightCounterPlural
                  ? sprintf(args.nightCounterPlural, dayCount)
                  : toolTipText
              } else {
                toolTipText = args.nightCounterSingular
                  ? sprintf(args.nightCounterSingular, dayCount)
                  : toolTipText
              }
            }

            cellEl.addEventListener(
              'mouseenter',
              function () {
                tippy(cellEl, {
                  content: toolTipText,
                  animation: 'shift-away-subtle',
                  theme: 'bhub',
                  zIndex: 999999999,
                  onHidden(instance) {
                    instance.destroy()
                  },
                }).show()

                cellEl.addEventListener('mouseleave', function () {
                  if (cellEl._tippy) {
                    cellEl._tippy.destroy()
                  }
                })
              },
              { once: true }
            )
          }
        }
      },
      onHide: () => {
        hideAll()
      },
    }

    const mergedArgs = {
      ...defaultOptions,
      ...args,
      onFocus: (event) => {
        if (typeof args.onFocus === 'function') {
          args.onFocus(event)
        }
        defaultOptions.onFocus(event)
      },
    }

    const hideInput = args.hideInput ?? false

    if (hideInput) {
      el.style.display = 'none'
    }

    super(el, mergedArgs)
    this.args = mergedArgs
  }

  dayCount = (dates) => {
    const d1 = dates[0]
    const d2 = dates[1]
    const diff = Math.abs(d1 - d2)
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  }

  static isRangeIncludesDisabledDay = (date, instance) => {
    const disabledDays = [...instance.disabledDates]
    const selectedDate = instance.selectedDates[0]

    if (selectedDate && instance.selectedDates.length === 1) {
      const sortedDates = [selectedDate, date].toSorted((a, b) => {
        if (a.getTime() > b.getTime()) {
          return 1
        }
        return -1
      })

      return disabledDays.some((disabledDate) => {
        return moment(disabledDate).isBetween(
          sortedDates[0],
          sortedDates[1],
          undefined,
          []
        )
      })
    }

    return false
  }

  static isOutOfLimit = (date, instance, minOrMax = 'max') => {
    const selectedDate = instance.selectedDates[0]
    const { minRange, maxRange } = instance.opts

    if (minRange === 0 && maxRange === 0) {
      return false
    }

    const sortedDates = [selectedDate, date].toSorted((a, b) => {
      if (a.getTime() > b.getTime()) {
        return 1
      }
      return -1
    })

    if (selectedDate && instance.selectedDates.length === 1) {
      const diff = moment(sortedDates[1]).diff(sortedDates[0], 'days')

      if (minOrMax === 'max') {
        return diff > maxRange
      }

      if (minOrMax === 'min') {
        return diff < minRange
      }

      if (minOrMax === 'both') {
        return diff < minRange || diff > maxRange
      }
    }

    return false
  }
}
